/* eslint-disable react/jsx-props-no-spreading */
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useMutation } from '@tanstack/react-query';
// Components
import {
  Button, Skeleton, TextField,
} from '@repo/zolintra-ui';
// Hooks
import { useTranslation } from 'react-i18next';
import useToast from '../../../../hooks/useToast';
import useFormUtils from '../../../../hooks/useFormUtils';
import { useAppContext } from '../../../../store/AppContext';
// Permissions
import hasPermission, { PERMISSIONS } from '../../../../permissions/hasPermission';
// Validation schema
import { configurationSchema } from '../../../../services/types';
// Services
import { useConfigurationQuery } from '../../../../services/configuration/configurationQueries';
import { patchConfiguration } from '../../../../services/configuration/configurationService';
// Styles
import styles from './DelayAndPlacesManagementForm.module.css';

function DelayAndPlacesManagementForm() {
  // Hooks
  const { t } = useTranslation();
  const [{ user }] = useAppContext();
  const toast = useToast();
  const { getFieldError } = useFormUtils();

  // Permissions
  const hasUpdatePermission = hasPermission(PERMISSIONS.UPDATE_CONFIGURATION, user.prm);

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm({
    resolver: joiResolver(configurationSchema),
    mode: 'onBlur',
    shouldFocusError: false,
  });

  // API Calls
  const configurationQuery = useConfigurationQuery();
  const configurationMutation = useMutation({
    mutationFn: patchConfiguration,
    onSuccess: () => {
      configurationQuery.refetch();
      toast.notify({
        type: 'success',
        message: t('settings.configurationUpdateSuccess'),
      });
    },
  });

  // Effects
  useEffect(() => {
    if (configurationQuery.isSuccess && configurationQuery.data) {
      reset(configurationQuery.data);
    }
  }, [configurationQuery.data, configurationQuery.isSuccess]);

  // Handlers
  const onSubmit = (values) => configurationMutation.mutate(values);

  return (
    <form className={styles.form}>
      <section className={styles.grid}>
        <fieldset className={styles.card}>
          <span className={styles['card-title']}>{t('settings.leaveFieldsLabel')}</span>
          {configurationQuery.isPending
            ? (
              <>
                <Skeleton className="margin-bottom-xs" shape="rectangle" width="100%" height="1rem" />
                <Skeleton className="margin-bottom-m" shape="rectangle" width="100%" height="3.625rem" />
                <Skeleton className="margin-bottom-xs" shape="rectangle" width="100%" height="1rem" />
                <Skeleton shape="rectangle" width="100%" height="3.625rem" />
              </>
            )
            : (
              <>
                <TextField
                  className="margin-bottom-m"
                  type="number"
                  label={t('settings.shortLeaveNoticeLabel')}
                  error={getFieldError({ errors: formErrors, fieldName: 'shortLeaveNoticePeriod' })}
                  min={1}
                  max={99}
                  {...register('shortLeaveNoticePeriod')}
                  disabled={!hasUpdatePermission}
                />
                <TextField
                  type="number"
                  label={t('settings.longLeaveNoticeLabel')}
                  error={getFieldError({ errors: formErrors, fieldName: 'longLeaveNoticePeriod' })}
                  min={1}
                  max={99}
                  {...register('longLeaveNoticePeriod')}
                  disabled={!hasUpdatePermission}
                />
              </>
            )}
        </fieldset>
        <fieldset className={styles.card}>
          <span className={styles['card-title']}>{t('settings.remoteWorkingFieldsLabel')}</span>
          {configurationQuery.isPending
            ? (
              <>
                <Skeleton className="margin-bottom-xs" shape="rectangle" width="100%" height="1rem" />
                <Skeleton className="margin-bottom-m" shape="rectangle" width="100%" height="3.625rem" />
                <Skeleton className="margin-bottom-xs" shape="rectangle" width="100%" height="1rem" />
                <Skeleton shape="rectangle" width="100%" height="3.625rem" />
              </>
            )
            : (
              <>
                <TextField
                  className="margin-bottom-m"
                  type="number"
                  label={t('settings.availableSeatsLabel')}
                  error={getFieldError({ errors: formErrors, fieldName: 'availableSeats' })}
                  min={1}
                  max={99}
                  {...register('availableSeats')}
                  disabled={!hasUpdatePermission}
                />
                <TextField
                  type="number"
                  label={t('settings.minimumPeopleOnSiteLabel')}
                  error={getFieldError({ errors: formErrors, fieldName: 'minResourcesOnSite' })}
                  min={1}
                  max={99}
                  {...register('minResourcesOnSite')}
                  disabled={!hasUpdatePermission}
                />
              </>
            )}
        </fieldset>
      </section>

      {hasUpdatePermission ? (
        <section className={styles.actions}>
          <Button
            onClick={handleSubmit(onSubmit)}
            isLoading={configurationMutation.isPending}
          >
            {t('settings.formAction')}
          </Button>
        </section>
      ) : null}
    </form>
  );
}

DelayAndPlacesManagementForm.propTypes = {};

export default DelayAndPlacesManagementForm;
